import { BoxImage } from "@loot-os/ui/components/box-image";
import { Card } from "@loot-os/ui/components/card";
import { cn } from "@loot-os/ui/lib/utils";
import type { ReactNode } from "react";

/**
 * One item won by an open, as the storefront's recent-opens row shows it.
 *
 * The tier color washes the card rather than only edging it, which is what makes a row of these
 * readable at a glance. It arrives as `#rrggbb` from `resolveRarityColor`, so the alpha suffix
 * below is safe to concatenate — see api-contract's rarityColor.ts for why that matters.
 *
 * Renders no link, like `BoxCard`: the two SPAs have different routers.
 */
export function OpenCard({
  item,
  rarityColor,
  badge,
  className,
}: {
  item: { name: string; imageUrl: string };
  /** The item's tier color as `#rrggbb`, from `resolveRarityColor`. */
  rarityColor: string;
  /** Optional corner label — the category the item came from, in the design. */
  badge?: ReactNode;
  className?: string;
}) {
  return (
    <Card
      // `border ring-0` swaps Card's own hairline ring for a border this can tint inline; the
      // ring's color is a class, and the tier color is only known at runtime.
      className={cn(
        "group @container relative h-full gap-3 overflow-hidden border py-0 ring-0",
        className,
      )}
      style={{ backgroundColor: `${rarityColor}33`, borderColor: `${rarityColor}4d` }}
    >
      <BoxImage
        src={item.imageUrl}
        alt={item.name}
        className="aspect-square w-full object-contain p-2 transition-transform group-hover:scale-105"
        fallback={<span className="text-3xl opacity-40">🃏</span>}
      />
      <div className="px-3 pb-3">
        {/* One line on a narrow card (the home row on a phone), two from 12rem wide, as the designs
            have it: the card's own width decides, not the screen's. */}
        <span className="line-clamp-1 text-lg leading-[1.125rem] font-bold text-foreground @min-[12rem]:line-clamp-2 @min-[12rem]:h-10">
          {item.name}
        </span>
      </div>
      {badge && (
        <span className="absolute top-2.5 left-2.5 flex h-5 items-center gap-1 rounded-full bg-secondary px-2 text-xs font-medium text-secondary-foreground">
          {badge}
        </span>
      )}
    </Card>
  );
}
